import React from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { HashLoader } from "react-spinners";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { useState } from "react";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import OutlinedInput from "@mui/material/OutlinedInput";
import MenuItem from "@mui/material/MenuItem";
import { styled } from "@mui/material/styles";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { BsArrowDownCircle, BsArrowUpCircle } from "react-icons/bs";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";

import { COLORS } from "../../utils/colors";
import { MainButton } from "../../components/MainButton";
import { updateEvent } from "../../api/events";
import { ICONS } from "../../utils/icons";
import { uploadImage } from "../../utils/uploadImage";

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: 250,
      minWidth: 250,
      fontFamily: "AuthenticSans90",
    },
  },
};

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} placement="left" />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: COLORS.veryLightMainAccent,
    color: "rgba(0, 0, 0, 0.87)",
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: "1px solid #dadde9",
  },
}));

export const CreateTicketCategoryDialog = ({
  openPopup,
  setOpenPopup,
  categories,
  handleSnackbar,
  currency,
  event,
  setEvent,
  supabase,
  userData,
}) => {
  const [loading, setLoading] = useState(false);
  const [showOptionalFields, setShowOptionalFields] = useState(false);
  const [image, setImage] = useState(null);

  const formik = useFormik({
    initialValues: {
      name: "",
      price: "",
      type: null,
      supply: "",
      qr_type: "static",
      overbooking_supply: null,
      description: null,
      behavior: {
        max_entry_scans: 1,
        max_exit_scans: 0,
        entry_days: 0,
      },
      tags: null,
      image: "",
      pwa_tickets: false,
    },
    validationSchema: yup.object({
      name: yup.string("Enter category name").required("Required"),
      price: yup.string("Enter price").required("Required"),
      type: yup.string("Enter type").required("Required"),
      supply: yup.number("Enter supply").required("Required"),
      qr_type: yup.string("Enter QR type").required("Required"),
    }),
    onSubmit: (values) => {
      handleFormSubmit(values);
    },
  });

  const handleClose = () => {
    setOpenPopup(false);
  };

  const handleSelectedFile = (event) => {
    setImage(event.target.files[0]);
  };

  const handleFormSubmit = async (values) => {
    try {
      setLoading(true);

      const confirmAction = window.confirm(
        "Are you sure you want to take this action?"
      );

      if (!confirmAction) {
        setLoading(false);
        return;
      }

      if (image) {
        const filePaths = await uploadImage({
          incomingFiles: image,
          supabase,
          userData,
          handleSnackbar,
        });

        values.image = filePaths[0];
      }

      if (
        values.behavior.max_entry_scans === "" ||
        values.behavior.max_entry_scans === "" ||
        values.behavior.entry_days === ""
      ) {
        handleSnackbar(
          "No behavior field is allowed to be empty. Set to 0 as default values.",
          "error"
        );
        setLoading(false);
        return;
      }

      let updates = {};
      updates["ticket_categories"] = categories;
      updates["ticket_categories"].push(values);

      /**
       * The backend expects all the ticket categogries to have the supply field as a number, not object. So we need to convert it.
       */
      for (let i = 0; i < updates.ticket_categories.length; i++) {
        if (typeof updates.ticket_categories[i].supply === "object") {
          updates.ticket_categories[i].supply =
            updates.ticket_categories[i].supply.total;
        }
        if (
          typeof updates.ticket_categories[i].overbooking_supply === "object"
        ) {
          updates.ticket_categories[i].overbooking_supply =
            updates.ticket_categories[i].overbooking_supply?.total;
        }
      }

      const { data, error } = await updateEvent(updates, event);

      if (data) {
        setEvent(data);
      }

      if (error) {
        handleSnackbar(error.message, "error");
        setLoading(false);
        return;
      }

      handleSnackbar("Ticket category updated successfully", "success");
      handleClose();
      setLoading(false);
    } catch (error) {
      console.log("error: ", error);
      handleSnackbar(error, "error");
      setLoading(false);
    }
  };

  return (
    <Dialog open={openPopup} onClose={() => handleClose()}>
      <DialogContent
        sx={{
          width: "500px",
          padding: "40px",
          alignItems: "center",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <form style={{ width: "500px" }}>
          <h2 style={{ width: "-webkit-fill-available", marginBottom: "20px" }}>
            {ICONS.plusBlack25} Create new ticket category
          </h2>
          <p style={{ width: "-webkit-fill-available", marginBottom: "20px" }}>
            Required Fields
          </p>
          <div style={{ width: "-webkit-fill-available" }}>
            <div className="field-container">
              <p
                id="subheader"
                style={{
                  margin: "0px 0px 3px",
                  alignSelf: "center",
                }}
              >
                Name*
              </p>
              <TextField
                fullWidth
                disabled={loading}
                size="small"
                className="input"
                name="name"
                placeholder="not set"
                value={formik.values.name}
                onChange={formik.handleChange}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                sx={{
                  "& label": {
                    paddingLeft: (theme) => theme.spacing(1),
                  },
                  "& fieldset": {
                    borderRadius: "6px",
                    border: `2px solid #f0f1f5`,
                    transition: ".2s ease-in-out all",
                  },
                  "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor: "#f0f1f5",
                    },
                  "& .MuiOutlinedInput-input:hover": {
                    cursor: loading && "not-allowed",
                  },
                }}
              />
            </div>
            <div className="field-container">
              <p
                id="subheader"
                style={{
                  margin: "0px 0px 3px",
                  alignSelf: "center",
                }}
              >
                Listing Price* ({currency})
              </p>
              <TextField
                fullWidth
                disabled={loading}
                size="small"
                className="input"
                name="price"
                type="number"
                inputProps={{ min: 0 }}
                placeholder="not set"
                value={formik.values.price}
                onChange={formik.handleChange}
                error={formik.touched.price && Boolean(formik.errors.price)}
                helperText={formik.touched.price && formik.errors.price}
                sx={{
                  "& label": {
                    paddingLeft: (theme) => theme.spacing(1),
                  },
                  "& fieldset": {
                    borderRadius: "6px",
                    border: `2px solid #f0f1f5`,
                    transition: ".2s ease-in-out all",
                  },
                  "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor: "#f0f1f5",
                    },
                  "& .MuiOutlinedInput-input:hover": {
                    cursor: loading && "not-allowed",
                  },
                }}
              />
            </div>
            <div className="field-container">
              <p
                id="subheader"
                style={{
                  margin: "0px 0px 3px",
                  alignSelf: "center",
                }}
              >
                Type*
              </p>
              <Select
                size="small"
                name="type"
                value={formik.values.type}
                onChange={formik.handleChange}
                input={<OutlinedInput label="Name" />}
                MenuProps={MenuProps}
                style={{
                  maxWidth: "100px",
                  minWidth: "-webkit-fill-available",
                }}
                sx={{
                  "& label": {
                    paddingLeft: (theme) => theme.spacing(1),
                  },
                  "& fieldset": {
                    borderRadius: "6px",
                    border: `2px solid #f0f1f5`,
                    transition: ".2s ease-in-out all",
                  },
                  "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor: "#f0f1f5",
                    },
                  "& .MuiOutlinedInput-input:hover": {
                    cursor: loading && "not-allowed",
                  },
                }}
              >
                <MenuItem value={"general_admission"}>
                  General Admission
                </MenuItem>
                {/* <MenuItem value={"seated_ticket"}>Seated Ticket</MenuItem> */}
              </Select>
            </div>
            <div className="field-container">
              <p
                id="subheader"
                style={{
                  margin: "0px 0px 3px",
                  alignSelf: "center",
                }}
              >
                Supply*
              </p>
              <TextField
                fullWidth
                disabled={loading}
                size="small"
                className="input"
                name="supply"
                type="number"
                inputProps={{ min: 0 }}
                placeholder="not set"
                value={formik.values.supply}
                onChange={formik.handleChange}
                error={formik.touched.supply && Boolean(formik.errors.supply)}
                helperText={formik.touched.supply && formik.errors.supply}
                sx={{
                  "& label": {
                    paddingLeft: (theme) => theme.spacing(1),
                  },
                  "& fieldset": {
                    borderRadius: "6px",
                    border: `2px solid #f0f1f5`,
                    transition: ".2s ease-in-out all",
                  },
                  "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor: "#f0f1f5",
                    },
                  "& .MuiOutlinedInput-input:hover": {
                    cursor: loading && "not-allowed",
                  },
                }}
              />
            </div>
            <div className="field-container">
              <p
                id="subheader"
                style={{
                  margin: "0px 0px 3px",
                  alignSelf: "center",
                }}
              >
                QR Code Type*
              </p>
              <Select
                size="small"
                name="qr_type"
                value={formik.values.qr_type}
                onChange={formik.handleChange}
                input={<OutlinedInput label="Name" />}
                MenuProps={MenuProps}
                style={{
                  maxWidth: "100px",
                  minWidth: "-webkit-fill-available",
                }}
                sx={{
                  "& label": {
                    paddingLeft: (theme) => theme.spacing(1),
                  },
                  "& fieldset": {
                    borderRadius: "6px",
                    border: `2px solid #f0f1f5`,
                    transition: ".2s ease-in-out all",
                  },
                  "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor: "#f0f1f5",
                    },
                  "& .MuiOutlinedInput-input:hover": {
                    cursor: loading && "not-allowed",
                  },
                }}
              >
                <MenuItem value={"static"}>Static</MenuItem>
                <MenuItem value={"dynamic"}>Dynamic</MenuItem>
              </Select>
            </div>
          </div>
          {showOptionalFields ? (
            <p
              style={{
                width: "-webkit-fill-available",
                margin: "20px 0px",
                cursor: "pointer",
                alignItems: "center",
                display: "flex",
                gap: "5px",
              }}
              onClick={() => setShowOptionalFields(!showOptionalFields)}
            >
              <BsArrowUpCircle size={18} /> Hide Optional Fields
            </p>
          ) : (
            <p
              style={{
                width: "-webkit-fill-available",
                margin: "20px 0px",
                cursor: "pointer",
                alignItems: "center",
                display: "flex",
                gap: "5px",
              }}
              onClick={() => setShowOptionalFields(!showOptionalFields)}
            >
              <BsArrowDownCircle size={18} /> Show Optional Fields
            </p>
          )}
          {showOptionalFields && (
            <>
              <FormGroup style={{ marginBottom: "20px" }}>
                <FormControlLabel
                  sx={{
                    "& .MuiTypography-root": {
                      fontFamily: "AuthenticSans90",
                    },
                  }}
                  control={
                    <Checkbox
                      name={"pwa_tickets"}
                      checked={formik.values.pwa_tickets}
                      onChange={formik.handleChange}
                    />
                  }
                  label="LiveSync tickets"
                />
                <p style={{ fontSize: "14px", color: "grey" }}>
                  Enable or disable LiveSync Tickets for the ticket holders.
                </p>
              </FormGroup>
              <div className="field-container">
                <p
                  id="subheader"
                  style={{
                    margin: "0px 0px 3px",
                    alignSelf: "center",
                  }}
                >
                  Description
                </p>
                <TextField
                  fullWidth
                  disabled={loading}
                  size="small"
                  className="input"
                  name="description"
                  placeholder="not set"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.description &&
                    Boolean(formik.errors.description)
                  }
                  helperText={
                    formik.touched.description && formik.errors.description
                  }
                  sx={{
                    "& label": {
                      paddingLeft: (theme) => theme.spacing(1),
                    },
                    "& fieldset": {
                      borderRadius: "6px",
                      border: `2px solid #f0f1f5`,
                      transition: ".2s ease-in-out all",
                    },
                    "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                      {
                        borderColor: "#f0f1f5",
                      },
                    "& .MuiOutlinedInput-input:hover": {
                      cursor: loading && "not-allowed",
                    },
                  }}
                />
              </div>
              <div className="field-container">
                <HtmlTooltip
                  title={
                    <React.Fragment>
                      <Typography color="inherit">
                        Overbooking Supply - Definition
                      </Typography>
                      {"The"} <b>{"overbooking supply"}</b>{" "}
                      {
                        " controls if tickets can be sold after the supply has been reached, to cover for no-show ticket buyers."
                      }
                    </React.Fragment>
                  }
                >
                  <p
                    id="subheader"
                    style={{
                      margin: "0px 0px 6px",
                      alignSelf: "center",
                      alignItems: "center",
                      display: "flex",
                      gap: "5px",
                    }}
                  >
                    Overvooking Supply
                    <AiOutlineInfoCircle />
                  </p>
                </HtmlTooltip>
                <TextField
                  fullWidth
                  disabled={loading}
                  size="small"
                  className="input"
                  name="overbooking_supply"
                  type="number"
                  inputProps={{ min: 0 }}
                  placeholder="not set"
                  value={formik.values.overbooking_supply}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.overbooking_supply &&
                    Boolean(formik.errors.overbooking_supply)
                  }
                  helperText={
                    formik.touched.overbooking_supply &&
                    formik.errors.overbooking_supply
                  }
                  sx={{
                    "& label": {
                      paddingLeft: (theme) => theme.spacing(1),
                    },
                    "& fieldset": {
                      borderRadius: "6px",
                      border: `2px solid #f0f1f5`,
                      transition: ".2s ease-in-out all",
                    },
                    "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                      {
                        borderColor: "#f0f1f5",
                      },
                    "& .MuiOutlinedInput-input:hover": {
                      cursor: loading && "not-allowed",
                    },
                  }}
                />
              </div>
              <div className="field-container">
                <HtmlTooltip
                  title={
                    <React.Fragment>
                      <Typography color="inherit">
                        Ticket behavior - Definition
                      </Typography>
                      {"The"} <b>{"behavior fields"}</b>{" "}
                      {
                        " define how the ticket behaves. By default, the ticket is valid for all days and all zones with unlimited entry and exit scans."
                      }
                    </React.Fragment>
                  }
                >
                  <p
                    id="subheader"
                    style={{
                      margin: "0px 0px 6px",
                      alignSelf: "center",
                      alignItems: "center",
                      display: "flex",
                      gap: "5px",
                    }}
                  >
                    Ticket behavior
                    <AiOutlineInfoCircle />
                  </p>
                </HtmlTooltip>
                <div className="container-row" style={{ padding: "0 15px" }}>
                  <div>
                    <HtmlTooltip
                      title={
                        <React.Fragment>
                          <Typography color="inherit">
                            Max. Scans - Definition
                          </Typography>
                          {"The"} <b>{"max. scans"}</b>{" "}
                          {
                            " field defines how many times the ticket can be scanned. Not set means unlimited."
                          }
                        </React.Fragment>
                      }
                    >
                      <p
                        id="subheader"
                        style={{
                          margin: "0px 0px 6px",
                          alignSelf: "center",
                          alignItems: "center",
                          display: "flex",
                          gap: "5px",
                        }}
                      >
                        Max. scans*
                        <AiOutlineInfoCircle />
                      </p>
                    </HtmlTooltip>
                    <TextField
                      fullWidth
                      disabled={loading}
                      size="small"
                      className="input"
                      name="behavior.max_entry_scans"
                      type="number"
                      inputProps={{ min: 0 }}
                      placeholder="not set"
                      value={formik.values.behavior?.max_entry_scans}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.behavior?.max_entry_scans &&
                        Boolean(formik.errors.behavior?.max_entry_scans)
                      }
                      helperText={
                        formik.touched.behavior?.max_entry_scans &&
                        formik.errors.behavior?.max_entry_scans
                      }
                      sx={{
                        "& label": {
                          paddingLeft: (theme) => theme.spacing(1),
                        },
                        "& fieldset": {
                          borderRadius: "6px",
                          border: `2px solid #f0f1f5`,
                          transition: ".2s ease-in-out all",
                        },
                        "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                          {
                            borderColor: "#f0f1f5",
                          },
                        "& .MuiOutlinedInput-input:hover": {
                          cursor: loading && "not-allowed",
                        },
                      }}
                    />
                  </div>
                  {/* <div>
                    <HtmlTooltip
                      title={
                        <React.Fragment>
                          <Typography color="inherit">
                            Max. Exit Scans - Definition
                          </Typography>
                          {"The"} <b>{"max. exit scans"}</b>{" "}
                          {
                            " field defines how many times the ticket can be scanned at the exit. Not set means unlimited."
                          }
                        </React.Fragment>
                      }
                    >
                      <p
                        id="subheader"
                        style={{
                          margin: "0px 0px 6px",
                          alignSelf: "center",
                          alignItems: "center",
                          display: "flex",
                          gap: "5px",
                        }}
                      >
                        Max. exit scans*
                        <AiOutlineInfoCircle />
                      </p>
                    </HtmlTooltip>
                    <TextField
                      fullWidth
                      disabled={loading}
                      size="small"
                      className="input"
                      name="behavior.max_exit_scans"
                      type="number"
                      inputProps={{ min: 0 }}
                      placeholder="not set"
                      value={formik.values.behavior?.max_exit_scans}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.behavior?.max_exit_scans &&
                        Boolean(formik.errors.behavior?.max_exit_scans)
                      }
                      helperText={
                        formik.touched.behavior?.max_exit_scans &&
                        formik.errors.behavior?.max_exit_scans
                      }
                      sx={{
                        "& label": {
                          paddingLeft: (theme) => theme.spacing(1),
                        },
                        "& fieldset": {
                          borderRadius: "6px",
                          border: `2px solid #f0f1f5`,
                          transition: ".2s ease-in-out all",
                        },
                        "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                          {
                            borderColor: "#f0f1f5",
                          },
                        "& .MuiOutlinedInput-input:hover": {
                          cursor: loading && "not-allowed",
                        },
                      }}
                    />
                  </div> */}
                  {/* <div>
                    <HtmlTooltip
                      title={
                        <React.Fragment>
                          <Typography color="inherit">
                            Entry Days - Definition
                          </Typography>
                          {"The"} <b>{"entry days"}</b>{" "}
                          {
                            " field defines the dayy when ticket is allowed to enter the event. Accepted values: 0 = all days, 1 = day 1, 2 = day 2 etc."
                          }
                        </React.Fragment>
                      }
                    >
                      <p
                        id="subheader"
                        style={{
                          margin: "0px 0px 6px",
                          alignSelf: "center",
                          alignItems: "center",
                          display: "flex",
                          gap: "5px",
                        }}
                      >
                        Entry Days*
                        <AiOutlineInfoCircle />
                      </p>
                    </HtmlTooltip>
                    <TextField
                      fullWidth
                      disabled={loading}
                      size="small"
                      className="input"
                      name="behavior.entry_days"
                      type="number"
                      inputProps={{ min: 0 }}
                      placeholder="not set"
                      value={formik.values.behavior?.entry_days}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.behavior?.entry_days &&
                        Boolean(formik.errors.behavior?.entry_days)
                      }
                      helperText={
                        formik.touched.behavior?.entry_days &&
                        formik.errors.behavior?.entry_days
                      }
                      sx={{
                        "& label": {
                          paddingLeft: (theme) => theme.spacing(1),
                        },
                        "& fieldset": {
                          borderRadius: "6px",
                          border: `2px solid #f0f1f5`,
                          transition: ".2s ease-in-out all",
                        },
                        "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                          {
                            borderColor: "#f0f1f5",
                          },
                        "& .MuiOutlinedInput-input:hover": {
                          cursor: loading && "not-allowed",
                        },
                      }}
                    />
                  </div> */}
                </div>
              </div>
              <div className="field-container">
                <p
                  id="subheader"
                  style={{
                    margin: "0px 0px 3px",
                    alignSelf: "center",
                  }}
                >
                  Tags (separated by comma)
                </p>
                <TextField
                  fullWidth
                  disabled={loading}
                  size="small"
                  className="input"
                  name="tags"
                  placeholder="not set"
                  value={formik.values.tags}
                  onChange={formik.handleChange}
                  error={formik.touched.tags && Boolean(formik.errors.tags)}
                  helperText={formik.touched.tags && formik.errors.tags}
                  sx={{
                    "& label": {
                      paddingLeft: (theme) => theme.spacing(1),
                    },
                    "& fieldset": {
                      borderRadius: "6px",
                      border: `2px solid #f0f1f5`,
                      transition: ".2s ease-in-out all",
                    },
                    "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                      {
                        borderColor: "#f0f1f5",
                      },
                    "& .MuiOutlinedInput-input:hover": {
                      cursor: loading && "not-allowed",
                    },
                  }}
                />
              </div>
              <div className="field-container">
                <p
                  id="subheader"
                  style={{
                    margin: "0px 0px 3px",
                    alignSelf: "center",
                  }}
                >
                  Image
                </p>
                <input
                  type="file"
                  name="image"
                  className="input-upload"
                  onChange={handleSelectedFile}
                  placeholder="Upload logo"
                />
              </div>
            </>
          )}
          <DialogActions>
            <MainButton
              disabled={loading}
              color={COLORS.black}
              shadowColor={"transparent"}
              textColor={COLORS.white}
              textHoverColor={COLORS.lightGrey}
              hoverColor={COLORS.black}
              textAlignment={"center"}
              onClickAction={() => formik.handleSubmit()}
              width={"fit-content"}
            >
              {loading ? (
                <>
                  <HashLoader size={20} color={COLORS.grey} /> Save
                </>
              ) : (
                "Save"
              )}
            </MainButton>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
};
